/**
 * Where a console link from page content lands in the browser.
 *
 * Page content links to its targets with console URLs (`consoleLinks.ts` in
 * collab-protocol). Team pages, typed pages and types use the console's own
 * routes and need nothing here. The rest:
 *
 * - `/app/...` names something only its author's desktop holds (a session
 *   citation, a Personal page or typed page). The console cannot show it, so
 *   it offers the app through `nimbalyst://console/...`. A Claude Code
 *   citation (`/app/cite/claude-code/...`) is the exception: that session is
 *   on the author's machine and not in Nimbalyst at all, so there is nothing
 *   to hand off and the page only says where the quote came from.
 * - `/org/.../view/...` is a view placed in a page. A view of a type shows as
 *   that type's page, a marks list as the project's marks page.
 * - `/page/item/<KEY>` and the older `/trackers/item/<KEY>` carry an issue key
 *   where an item id is expected; `resolveTrackerItemRef` maps it.
 * - `/document/<id>?comment=<commentId>` cites a comment on a team page. It is
 *   the document route already; `documentAnchorSearch` makes the page open at
 *   that comment's thread.
 */
import {
  consoleLinkDeepLink,
  CONSOLE_LINK_ORIGIN,
  parseConsoleLink,
  type ConsoleLinkTarget,
} from '@nimbalyst/collab-protocol';

export type AppLinkLanding =
  | { kind: 'citation' | 'page' | 'item' | 'type' | 'view'; deepLink: string }
  /** No app hand-off: the cited Claude Code session is not one Nimbalyst holds. */
  | { kind: 'claude-code-citation'; deepLink: null };

function targetOf(pathname: string, search: string): { target: ConsoleLinkTarget; href: string } | null {
  const href = `${CONSOLE_LINK_ORIGIN}${pathname}${search}`;
  const target = parseConsoleLink(href);
  return target ? { target, href } : null;
}

/** The app hand-off for an `/app/...` link, or null when the link is malformed. */
export function appLinkLanding(pathname: string, search: string): AppLinkLanding | null {
  const parsed = targetOf(pathname, search);
  if (!parsed) return null;
  const { target, href } = parsed;
  // A comment citation is a team page link and opens in the console itself.
  if (target.kind === 'commentCitation') return null;
  if (target.kind !== 'citation' && target.scope !== 'local') return null;
  if (target.kind === 'citation' && target.agent === 'claude-code') return { kind: 'claude-code-citation', deepLink: null };
  const deepLink = consoleLinkDeepLink(href);
  return deepLink ? { kind: target.kind, deepLink } : null;
}

export type TeamViewLanding = { action: 'redirect'; path: string };

/** What a team `/view/...` link shows, or null when it is not one. */
export function teamViewLanding(pathname: string, search: string): TeamViewLanding | null {
  const parsed = targetOf(pathname, search);
  if (!parsed || parsed.target.kind !== 'view' || parsed.target.scope === 'local') return null;
  const { scope, view } = parsed.target;
  const project = `/org/${encodeURIComponent(scope.orgId)}/project/${encodeURIComponent(scope.projectId)}`;
  if (view.kind === 'type') return { action: 'redirect', path: `${project}/page/type/${encodeURIComponent(view.typeId)}` };
  return { action: 'redirect', path: view.marks === 'all' ? `${project}/marks` : `${project}/marks?kind=${view.marks}` };
}

/**
 * The document anchor a URL query asks for, with a comment citation's
 * `?comment=` read as the comment to open (`commentId`). An explicit
 * `commentId` wins; anything else is returned as it was.
 */
export function documentAnchorSearch(search: string): string {
  const params = new URLSearchParams(search);
  const cited = params.get('comment');
  if (!cited) return search;
  params.delete('comment');
  if (!params.has('commentId')) params.set('commentId', cited);
  return `?${params.toString()}`;
}

/** The item id an item route segment names: an id as itself, an issue key as its item. */
export function resolveTrackerItemRef(
  itemRef: string,
  recordsById: ReadonlyMap<string, { id: string; issueKey?: string | null }>,
): string | null {
  if (recordsById.has(itemRef)) return itemRef;
  for (const record of recordsById.values()) {
    if (record.issueKey === itemRef) return record.id;
  }
  return null;
}
